"use client";

import { CheckCircle2, Circle, Star } from "lucide-react";
import { formatPrice } from "@/utils/format-price";
import type { OptevenData } from "@/interface/Opteven";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";

export interface OptevenSectionProps {
    optevenData: OptevenData;
    wantsOpteven: boolean;
    isKmOver60k: boolean;
    onToggle: (value: boolean) => void;
}

export const OptevenSection = ({
    optevenData,
    wantsOpteven,
    isKmOver60k,
    onToggle,
}: OptevenSectionProps) => {
    return (
        <div className="space-y-3">
            <div>
                <h3 className="text-base font-bold text-gray-900">Estensione Garanzia Opteven</h3>
                <Dialog>
                    <DialogTrigger asChild>
                        <button className="text-sm text-blue-600 hover:text-blue-700 underline underline-offset-2 cursor-pointer mt-0.5 text-left">
                            Aggiungi una copertura extra per il tuo veicolo.
                        </button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="text-blue-600 text-xl font-bold">Garanzia Opteven</DialogTitle>
                            <DialogDescription className="pt-4 text-gray-700">
                                La garanzia Opteven XXL Super offre una copertura completa sui principali componenti meccanici ed elettronici del veicolo, inclusi motore, cambio, impianto elettrico, climatizzazione, trasmissione e sistemi elettronici. Non prevede franchigia né decurtazioni dovute all’usura del veicolo, con massimale pari al valore commerciale dell’auto.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="sm:justify-center mt-4">
                            <DialogClose asChild>
                                <Button type="button" className="bg-blue-600 text-white hover:bg-blue-700 px-8">
                                    Ok
                                </Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div
                onClick={() => {
                    if (isKmOver60k) return;
                    onToggle(!wantsOpteven);
                }}
                className={`rounded-2xl border-2 p-4 transition-all duration-200 flex items-center gap-4 ${
                    wantsOpteven
                        ? "border-blue-600 bg-blue-50/50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300"
                } ${isKmOver60k ? "opacity-90 cursor-not-allowed" : "cursor-pointer"}`}
            >
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${wantsOpteven ? "bg-blue-100" : "bg-gray-100"}`}>
                    <Star size={18} className={wantsOpteven ? "text-blue-600" : "text-gray-400"} />
                </div>
                <div className="flex-1 min-w-0">
                    <span className="font-bold text-gray-900 text-sm">Garanzia Opteven</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Un anno di copertura estesa.
                        {isKmOver60k && <span className="font-semibold text-blue-600 ml-1">(Obbligatoria)</span>}
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className="font-bold text-gray-900 text-sm whitespace-nowrap">
                        {formatPrice(parseFloat(String(optevenData.upprzven)))}
                    </span>
                    {wantsOpteven
                        ? <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        : <Circle className="h-5 w-5 text-gray-300" />
                    }
                </div>
            </div>
        </div>
    );
};
